/**
 * useBuoySelection - Custom Hook for Buoy Selection State Management
 * 
 * SOLID Principle: Single Responsibility Principle (SRP)
 * Purpose: Manages all buoy selection state and related operations,
 * separating this concern from the main dashboard component.
 * 
 * Responsibilities:
 * - Manage selected buoys state
 * - Manage selected sensors per buoy
 * - Manage selected fields per buoy
 * - Handle buoy toggle operations with proper state cleanup
 * - Provide merge capability detection
 */

import { useState, useCallback } from 'react';

export function useBuoySelection(allowedBuoys) {
  const [selectedBuoys, setSelectedBuoys] = useState([]);
  const [selectedSensors, setSelectedSensors] = useState({}); // { buoyId: [measurement] }
  const [selectedFields, setSelectedFields] = useState({});   // { buoyId: ['measurement:field'] }
  const [isMergeMode, setIsMergeMode] = useState(false);

  const toggleBuoy = useCallback((buoyId) => {
    if (!allowedBuoys.includes(buoyId)) return;
    
    const isCurrentlySelected = selectedBuoys.includes(buoyId);
    
    if (isCurrentlySelected) {
      // Removing buoy - batch all updates
      setSelectedBuoys(prev => prev.filter(b => b !== buoyId));
      setSelectedSensors(prev => {
        const next = { ...prev };
        delete next[buoyId];
        return next;
      });
      setSelectedFields(prev => {
        const next = { ...prev };
        delete next[buoyId];
        return next;
      });
    } else {
      // Adding buoy - batch all updates
      setSelectedBuoys(prev => [...prev, buoyId]);
      setSelectedSensors(prev => ({ ...prev, [buoyId]: [] }));
      setSelectedFields(prev => ({ ...prev, [buoyId]: [] }));
    }
  }, [selectedBuoys, allowedBuoys]);

  const handleToggleMerge = useCallback(() => {
    setIsMergeMode(prev => !prev);
  }, []);

  // Check if merge is possible (multiple buoys selected)
  const canMerge = selectedBuoys.length > 1;

  return {
    // State
    selectedBuoys,
    selectedSensors,
    selectedFields,
    isMergeMode,
    canMerge,
    
    // Actions
    toggleBuoy,
    setSelectedSensors,
    setSelectedFields,
    handleToggleMerge
  };
}